<script setup lang="ts">
import { ref } from 'vue'
import { VButton, VTextArea } from '@/modules/shared/components'

const props = defineProps<{
  mode: 'create' | 'edit' | 'reply'
  initialText?: string
  permission?: string
  onSubmit: (payload: { text: string; permission?: string }) => Promise<void>
  onCancel?: () => void
}>()

const text = ref(props.initialText || '')
const selectedPermission = ref(props.permission || 'everyone')
const submitting = ref(false)

const permissionOptions = [
  { label: 'Everyone', value: 'everyone' },
  { label: 'Investors', value: 'investors' },
  { label: 'Admins only', value: 'admins' },
]

const labels = {
  create: 'Add Note',
  edit: 'Save',
  reply: 'Reply',
}

const handleSubmit = async () => {
  if (!text.value.trim() || submitting.value) return
  submitting.value = true
  try {
    await props.onSubmit({
      text: text.value.trim(),
      permission: selectedPermission.value,
    })
    if (props.mode === 'create' || props.mode === 'reply') {
      text.value = ''
    }
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="rounded-lg border border-gray-200 bg-white p-4">
    <VTextArea
      v-model="text"
      :label="mode === 'reply' ? 'Write a reply...' : mode === 'edit' ? 'Edit note' : 'Write a note...'"
      placeholder="Type your note here..."
    />

    <div class="mt-3 flex items-center justify-between">
      <div v-if="!permission" class="flex items-center gap-2">
        <label class="text-sm font-medium text-gray-700">Visible to:</label>
        <select
          v-model="selectedPermission"
          class="rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
        >
          <option v-for="opt in permissionOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>
      <div v-else />

      <div class="flex gap-2">
        <VButton v-if="onCancel" :click="onCancel" size="sm" variant="v-white">Cancel</VButton>
        <VButton :click="handleSubmit" :disabled="!text.trim() || submitting" size="sm" variant="v-blue">
          {{ labels[mode] }}
        </VButton>
      </div>
    </div>
  </div>
</template>
